import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  operatorSchema,
  operatorSignInResponseSchema,
  type Operator,
  type OperatorPasswordCheckRequest,
  type OperatorSignInRequest,
} from '@forecastify/shared';
import { CONTROL_PANEL_PATHS, apiCall, type ApiInit, type ApiResult } from './api';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The operator session. `sessionStorage`, eight hours, nothing to refresh.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`sessionStorage`, NOT `localStorage`, AND THE CHOICE IS ABOUT THIS PRINCIPAL.** The token
 * is a bearer for an account that can read every tenant on the installation. `localStorage` survives
 * the browser closing, so a console left open on a shared machine stays signed in until the eight
 * hours run out; `sessionStorage` dies with the tab, which is the behaviour somebody would expect of
 * an admin console and *not* of a product they live in all day. `apps/web` keeps nothing in either —
 * its refresh token is an httpOnly cookie — so there is no precedent here to match.
 *
 * ⚠️ **Every read and write is wrapped, because a storage accessor can THROW.** Private windows,
 * "block site data", and some embedded contexts make `sessionStorage` itself throw on access rather
 * than returning null. An uncaught throw here happens before the first render, so it is a white page.
 *
 * ⚠️ **THE TOKEN IS IN A REF, NOT IN STATE.** `request` is handed to screens and called from
 * effects; a token in state means every consumer captures a stale closure over the value at render
 * time, and the symptom is one 401 immediately after signing in, on whichever screen rendered first.
 * `apps/web`'s session records the same finding.
 */

const STORAGE_KEY = 'forecastify.operator.token';

const readStored = (): string | null => {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value === null || value.trim() === '' ? null : value;
  } catch {
    return null;
  }
};

const writeStored = (token: string | null): void => {
  try {
    if (token === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* A session that cannot be persisted still works for this tab — the ref holds it. */
  }
};

export interface OperatorSessionState {
  /**
   * ⚠️ **Three states, and conflating any two is a bug somebody reports as "it hangs".**
   * `restoring` is the one that gets skipped: rendering the sign-in form while a stored token is
   * still being checked flashes a login screen at somebody who is signed in, on every reload.
   */
  readonly status: 'restoring' | 'signed-out' | 'signed-in';
  readonly operator: Operator | null;
  readonly signIn: (input: OperatorSignInRequest) => Promise<{ ok: boolean; code?: string }>;
  /**
   * 2 Nov 2026 · the password step's check, before the code step appears. Issues nothing — the
   * sign-in after it still sends all three together.
   */
  readonly checkPassword: (
    input: OperatorPasswordCheckRequest,
  ) => Promise<{ ok: boolean; code?: string }>;
  /**
   * `0179` · take a session the server issued some other way — the last step of joining from an
   * invitation answers with the same shape `signIn` does. `false` when the body is not that shape.
   */
  readonly adopt: (response: unknown) => boolean;
  readonly signOut: () => Promise<void>;
  /** An authenticated call. A 401 ends the session rather than being retried. */
  readonly request: <T>(path: string, init?: ApiInit) => Promise<ApiResult<T>>;
}

const SessionContext = createContext<OperatorSessionState | undefined>(undefined);

export function OperatorSessionProvider({ children }: { readonly children: ReactNode }) {
  const [status, setStatus] = useState<OperatorSessionState['status']>('restoring');
  const [operator, setOperator] = useState<Operator | null>(null);
  const token = useRef<string | null>(null);

  const end = useCallback((): void => {
    token.current = null;
    writeStored(null);
    setOperator(null);
    setStatus('signed-out');
  }, []);

  /**
   * ⚠️ **The restore is a real request, because a token is not evidence of a session.** An 8-hour
   * token in storage may be revoked (the operator was disabled), expired, or from a database that
   * has since been reset. `GET /control-panel/me` is the only thing that can tell.
   */
  useEffect(() => {
    const stored = readStored();
    if (stored === null) {
      setStatus('signed-out');
      return;
    }

    const alive = { current: true };

    void (async () => {
      const result = await apiCall<unknown>(CONTROL_PANEL_PATHS.me, { token: stored });
      if (!alive.current) return;

      if (!result.ok) {
        /* ⚠️ Including `unreachable`: we cannot prove there is a session, so we must not claim one.
         * The sign-in screen then fails loudly with a message when the API is still down, which is
         * more useful than a console that looks signed in and 401s on every screen. */
        end();
        return;
      }

      const parsed = operatorSchema.safeParse((result.value as { operator?: unknown }).operator);
      if (!parsed.success) {
        end();
        return;
      }

      token.current = stored;
      setOperator(parsed.data);
      setStatus('signed-in');
    })();

    return () => {
      /* ⚠️ A mutable object rather than a `let` flag: `hard-won.md` C15-3 — a reassignment inside a
       * closure the compiler cannot see running narrows the flag to `true`, and lint then reports
       * the guard as dead code, which invites deleting it. */
      alive.current = false;
    };
  }, [end]);

  const adopt = useCallback<OperatorSessionState['adopt']>((response) => {
    /**
     * ⚠️ **PARSED WITH THE SERVER'S OWN SCHEMA.** A `200` whose body is not the shape this expects
     * must not become a session: `token.current` would be `undefined`, every subsequent request
     * would 401, and the screen would report a refusal for a response that succeeded.
     */
    const parsed = operatorSignInResponseSchema.safeParse(response);
    if (!parsed.success) return false;

    token.current = parsed.data.session.token;
    writeStored(parsed.data.session.token);
    setOperator(parsed.data.operator);
    setStatus('signed-in');
    return true;
  }, []);

  const signIn = useCallback<OperatorSessionState['signIn']>(
    async (input) => {
      const result = await apiCall<unknown>(CONTROL_PANEL_PATHS.signIn, {
        method: 'POST',
        body: JSON.stringify(input),
      });

      if (!result.ok) return { ok: false, code: result.code };
      return adopt(result.value) ? { ok: true } : { ok: false, code: 'unknown' };
    },
    [adopt],
  );

  const checkPassword = useCallback<OperatorSessionState['checkPassword']>(async (input) => {
    const result = await apiCall<unknown>(CONTROL_PANEL_PATHS.signInPassword, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return result.ok ? { ok: true } : { ok: false, code: result.code };
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    const held = token.current;
    /* ⚠️ The local session ends whatever the server says. A network failure must not leave somebody
     * looking at a console they asked to leave. */
    if (held !== null) await apiCall(CONTROL_PANEL_PATHS.signOut, { method: 'POST', token: held });
    end();
  }, [end]);

  const request = useCallback<OperatorSessionState['request']>(
    async (path, init) => {
      const result = await apiCall(path, { ...init, token: token.current });

      /**
       * ⚠️ **A 401 ENDS THE SESSION HERE, and there is no retry.** With a refresh path this would be
       * "renew and try again"; there is none, so the only honest response is to stop. Without this,
       * every screen would render its own "unauthenticated" error and the operator would be left
       * clicking around a console that cannot answer anything.
       */
      if (!result.ok && result.status === 401) end();
      return result as ApiResult<never>;
    },
    [end],
  );

  const value = useMemo<OperatorSessionState>(
    () => ({ status, operator, signIn, checkPassword, adopt, signOut, request }),
    [status, operator, signIn, checkPassword, adopt, signOut, request],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useOperatorSession(): OperatorSessionState {
  const value = useContext(SessionContext);
  if (value === undefined) {
    throw new Error('useOperatorSession was called outside OperatorSessionProvider');
  }
  return value;
}
